/**
 * renameFormulaRefs.js — cascade a รหัสสูตร (OutputVar) rename through the formulas that reference it.
 *
 * Renaming a formula silently invalidates every OTHER formula pointing at the old code: they still
 * save, then die at calculation time with "unknown variable". This module finds them and repoints them.
 *
 * ── Why this is not a text replace ───────────────────────────────────────────────────
 * Two things make the naive version wrong, and both are silent:
 *   • `IF(note == 'expense_40_5', …)` — the code as a TEXT VALUE is data, not a reference. Tokenizing
 *     (never regex over the raw string) is what tells the two apart.
 *   • The code lives in TWO places: the Expression string AND the NAME fields inside
 *     ExpressionGraphJson. Rewrite only the string and chooseFormulaSource sees a graph that no longer
 *     recompiles to it, silently falls back to a rebuild, and discards the layout its author arranged.
 *     So the rename happens ON a workspace and both representations are re-emitted FROM it.
 *
 * Lives outside the view so it is testable headlessly — the guarantee above is worth a test, not a
 * one-off manual check.
 */
import Blockly from './blocklyCore.js'
import { tokenize } from '../formulaGraph.js'
import { ncalcToBlocks, chooseFormulaSource } from './ncalcToBlocks.js'
import { workspaceToExpression } from './ncalcGenerator.js'
import { GRAPH_VERSION } from '../formulaGraph.js'

/**
 * The formulas whose expression references `code`.
 * A formula whose expression will not tokenize is already broken and surfaces its own errors — it is
 * skipped rather than guessed at.
 * @param {Array<Object>} formulas       every formula of the calc version
 * @param {string} code                  the OutputVar being renamed
 * @param {number|null} excludeFormulaId the formula doing the renaming (never its own dependent)
 * @returns {Array<Object>} the referencing formulas, in the order given
 */
export function dependentsOf(formulas, code, excludeFormulaId) {
  const target = String(code == null ? '' : code).trim().toLowerCase()
  if (!target) return []
  return (Array.isArray(formulas) ? formulas : []).filter((f) => {
    if (!f || (excludeFormulaId != null && f.formulaID === excludeFormulaId)) return false
    let tokens
    try {
      tokens = tokenize(String(f.expression || ''))
    } catch (_err) {
      return false
    }
    return tokens.some((t) => t && t.type === 'ident' && String(t.value).toLowerCase() === target)
  })
}

/**
 * Point ONE formula at `newCode`, keeping the layout its author arranged.
 *
 * Matching on the block NAME alone is unambiguous: classifyField resolves outputVars BEFORE rule
 * bridges, and so does the engine (a formula's result overwrites a same-named bridge var in the bag),
 * so a block carrying this name IS this formula either way.
 *
 * @returns {{expression:string, graph:string}|null} null when the formula does not reference the code
 * @throws {Error} when the rewritten workspace will not compile — never returns a half-formula
 */
export function rewriteRefs(dep, oldCode, newCode) {
  const ws = new Blockly.Workspace()
  try {
    let blob = dep && dep.expressionGraphJson
    const expression = String((dep && dep.expression) || '')
    if (chooseFormulaSource(blob, expression).source === 'workspace') {
      if (typeof blob === 'string') {
        try { blob = JSON.parse(blob) } catch (_err) { blob = null }
      }
      if (blob && blob.workspace) Blockly.serialization.workspaces.load(blob.workspace, ws)
      else ncalcToBlocks(ws, expression, {})
    } else {
      ncalcToBlocks(ws, expression, {})
    }

    const want = String(oldCode).toLowerCase()
    let touched = 0
    ws.getAllBlocks(false).forEach((b) => {
      if (!b || b.type !== 'tax_variable') return
      if (String(b.getFieldValue('NAME') || '').toLowerCase() !== want) return
      b.setFieldValue(newCode, 'NAME')
      touched += 1
    })
    if (!touched) return null

    const compiled = workspaceToExpression(ws)
    if (compiled.errors.length || !compiled.expression) {
      throw new Error(compiled.errors[0] || 'คอมไพล์สูตรที่แก้ไม่สำเร็จ')
    }
    return {
      expression: compiled.expression,
      graph: JSON.stringify({
        graphVersion: GRAPH_VERSION,
        workspace: Blockly.serialization.workspaces.save(ws),
      }),
    }
  } finally {
    try { ws.dispose() } catch (_err) { /* headless workspace — ignore */ }
  }
}

export default { dependentsOf, rewriteRefs }
